import React, { useEffect, useState, useCallback, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import localforage from 'localforage';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Fade,
  Grid,
  Container,
  Alert,
  Badge,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import {
  Add as AddIcon,
  QrCodeScanner as QrCodeIcon,
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
  EventAvailable as EventAvailableIcon,
} from "@mui/icons-material";
import {
  fetchMainUserByEmail,
  fetchChildUserByEmail,
  fetchUserByStudentID,
  fetchFamilyByID,
  fetchUnreadMessageCount,
  fetchUserAttendanceStats,
} from "../utils/api/api";
import { SwitchUserOverlay } from "../utils/components/overlays/SwitchUserOverlay";
import { useClubScope } from "../contexts/ClubScopeContext";
import { isCashPaymentPending } from "../utils/cashPaymentPending";
import PageLoader from "../utils/components/PageLoader";
import { WarningCard } from "../utils/components/cards/WarningCard";
import SupabaseImageAvatar from "../utils/components/SupabaseImageAvatar";
import { useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import { SupabaseUserContext } from "../contexts/supabaseUser.context";
import { getBeltAccentColor } from "../utils/utils/membersBeltUtils";
import StaffDailyDashboard from "./dashboard/StaffDailyDashboard";
import LandingFamilySwitcher from "./LandingFamilySwitcher";
import LandingFlipMemberCard from "./LandingFlipMemberCard";
import { useActiveMemberSport } from "../hooks/useActiveMemberSport";
import { memberShowsBeltRankMenu } from "../utils/memberSport";
import {
  shouldShowStaffDashboard,
  findLoggedInAccount,
  userShowsStaffDashboard,
} from "../realm/settingsPermissions";
import tokenManager from "../utils/security/tokenManager";
import {
  LANDING_ACCESS,
  getFetchFailureInfo,
  resolveAccessBlockAfterFetch,
  resolveAccessBlockFromLoadError,
} from "../utils/landingAccess";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import LoginIcon from "@mui/icons-material/Login";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { getMm } from "../theme/modernMaterialEnterprise";
import {
  getFocusVisibleSx,
  getGlassSurfaceSx,
  getInteractiveCardSx,
  getLandingKpiTint,
  getMotionTransition,
} from "../theme/landingDashboardStyles";

function goToLoginPage() {
  tokenManager.clearTokens();
  try {
    localStorage.removeItem("email");
  } catch {
    /* ignore */
  }
  window.location.href = "/login";
}

function LandingStatusCard({
  type,
  archived = false,
  isDarkMode,
  t,
  onRefresh,
  outlineButtonSx,
  primaryButtonSx,
}) {
  const cardShellSx = {
    borderRadius: getMm(isDarkMode).radiusMd,
    ...getGlassSurfaceSx(isDarkMode, { elevated: true }),
    mb: 4,
    fontFamily: getMm(isDarkMode).fontBody,
  };

  const statusCardActionSx = {
    minHeight: 40,
    py: 1,
    px: 2,
    fontSize: "0.875rem",
    lineHeight: 1.3,
    flex: { xs: "none", sm: "1 1 0" },
    minWidth: { sm: 0 },
    width: { xs: "100%", sm: "auto" },
    "& .MuiButton-startIcon": {
      marginRight: "6px",
      "& svg": { fontSize: "1.1rem" },
    },
  };

  const statusConfig = {
    [LANDING_ACCESS.UNDER_REVIEW]: {
      Icon: PendingActionsIcon,
      iconColor: isDarkMode ? "#FBBF24" : "#D97706",
      iconBg: isDarkMode ? "rgba(245, 158, 11, 0.12)" : "rgba(245, 158, 11, 0.1)",
      title: t("account_review_status_title"),
      heading: t("account_review_status_heading"),
      body: t("account_review_notice"),
      primary: {
        label: t("refresh"),
        onClick: onRefresh,
        icon: RefreshIcon,
        variant: "outlined",
        sx: outlineButtonSx,
      },
    },
    [LANDING_ACCESS.ACCOUNT_NOT_FOUND]: {
      Icon: PersonOffIcon,
      iconColor: getMm(isDarkMode).onSurfaceVariant,
      iconBg: isDarkMode ? "rgba(156, 163, 175, 0.12)" : "rgba(107, 114, 128, 0.1)",
      title: t("landing_account_not_found_title"),
      heading: archived ? t("landing_account_archived_heading") : t("landing_account_not_found_heading"),
      body: archived ? t("landing_account_archived_message") : t("landing_account_not_found_message"),
      primary: { label: t("landing_go_to_login"), onClick: goToLoginPage, icon: LoginIcon, sx: primaryButtonSx },
      secondary: { label: t("refresh"), onClick: onRefresh, icon: RefreshIcon, sx: outlineButtonSx },
    },
    [LANDING_ACCESS.SESSION_EXPIRED]: {
      Icon: LoginIcon,
      iconColor: getMm(isDarkMode).secondary,
      iconBg: isDarkMode ? "rgba(132, 212, 211, 0.16)" : "rgba(0, 106, 106, 0.1)",
      title: t("landing_session_expired_title"),
      heading: t("landing_session_expired_heading"),
      body: t("landing_session_expired_message"),
      primary: { label: t("landing_go_to_login"), onClick: goToLoginPage, icon: LoginIcon, sx: primaryButtonSx },
      secondary: { label: t("refresh"), onClick: onRefresh, icon: RefreshIcon, sx: outlineButtonSx },
    },
    [LANDING_ACCESS.CONNECTION_ERROR]: {
      Icon: CloudOffIcon,
      iconColor: getMm(isDarkMode).error,
      iconBg: isDarkMode ? "rgba(239, 68, 68, 0.12)" : "rgba(239, 68, 68, 0.08)",
      title: t("landing_connection_error_title"),
      heading: t("landing_connection_error_heading"),
      body: t("landing_connection_error_message"),
      primary: {
        label: t("refresh"),
        onClick: onRefresh,
        icon: RefreshIcon,
        variant: "contained",
        sx: primaryButtonSx,
      },
      secondary: {
        label: t("landing_go_to_login"),
        onClick: goToLoginPage,
        icon: LoginIcon,
        variant: "outlined",
        sx: outlineButtonSx,
      },
    },
    [LANDING_ACCESS.NOT_LOGGED_IN]: {
      Icon: LoginIcon,
      iconColor: getMm(isDarkMode).secondary,
      iconBg: isDarkMode ? "rgba(132, 212, 211, 0.16)" : "rgba(0, 106, 106, 0.1)",
      title: t("landing_not_logged_in_title"),
      heading: t("landing_not_logged_in_heading"),
      body: t("landing_not_logged_in_message"),
      primary: { label: t("landing_go_to_login"), onClick: goToLoginPage, icon: LoginIcon, sx: primaryButtonSx },
    },
  };

  const config = statusConfig[type] || statusConfig[LANDING_ACCESS.UNDER_REVIEW];
  if (!config) return null;

  const { Icon, iconColor, iconBg, title, heading, body, primary, secondary } = config;
  const PrimaryIcon = primary?.icon;
  const SecondaryIcon = secondary?.icon;

  return (
    <Card sx={cardShellSx}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
            backgroundColor: iconBg,
            border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
          }}
        >
          <Icon sx={{ fontSize: 36, color: iconColor }} />
        </Box>
        <Typography
          sx={{
            color: getMm(isDarkMode).onSurfaceVariant,
            fontWeight: 600,
            mb: 1,
            fontSize: "0.875rem",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            color: getMm(isDarkMode).onSurface,
            mb: 1.5,
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {heading}
        </Typography>
        <Typography
          sx={{
            color: getMm(isDarkMode).onSurfaceVariant,
            fontWeight: 500,
            lineHeight: 1.6,
            fontSize: "0.875rem",
            maxWidth: 480,
            mb: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {body}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.25,
            width: "100%",
            maxWidth: 420,
            justifyContent: "center",
          }}
        >
          {secondary ? (
            <Button
              variant={secondary.variant || "outlined"}
              startIcon={SecondaryIcon ? <SecondaryIcon /> : undefined}
              onClick={secondary.onClick}
              sx={{
                ...secondary.sx,
                ...statusCardActionSx,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {secondary.label}
            </Button>
          ) : null}
          {primary ? (
            <Button
              variant={primary.variant || "contained"}
              startIcon={PrimaryIcon ? <PrimaryIcon /> : undefined}
              onClick={primary.onClick}
              sx={{
                ...primary.sx,
                ...statusCardActionSx,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {primary.label}
            </Button>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}

function parseMemberAttendanceStats(data) {
  if (!data || typeof data !== "object") {
    return { totalVisits: null, lastVisit: null };
  }
  const totalVisits =
    typeof data.totalVisits === "number"
      ? data.totalVisits
      : typeof data.total_visits === "number"
        ? data.total_visits
        : typeof data.totalRecords === "number"
          ? data.totalRecords
          : null;
  const lastVisit = data.lastVisit || data.last_visit || null;
  return { totalVisits, lastVisit };
}

function getCurrentMonthDateRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return {
    startDate: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
    endDate: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`,
  };
}

function getDaysSinceVisit(lastVisit) {
  if (!lastVisit) return null;
  const date = new Date(lastVisit);
  if (Number.isNaN(date.getTime())) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfVisit = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((startOfToday - startOfVisit) / (24 * 60 * 60 * 1000));
}

function getActivityCopy(lastVisit, t) {
  const days = getDaysSinceVisit(lastVisit);
  if (days == null && !lastVisit) {
    return {
      title: t("landing_activity_no_visits"),
      hint: t("landing_activity_no_visits_hint"),
    };
  }
  if (days === 0) {
    return {
      title: t("landing_activity_active_today"),
      hint: t("landing_activity_active_today_hint"),
    };
  }
  if (days != null && days < 7) {
    return { title: t("landing_activity_this_week"), hint: null };
  }
  if (days != null && days < 21) {
    return { title: t("landing_activity_get_back"), hint: null };
  }
  return { title: t("landing_activity_miss_you"), hint: null };
}

function formatRelativeLastVisit(lastVisit, t, locale) {
  if (!lastVisit) return null;
  const date = new Date(lastVisit);
  if (Number.isNaN(date.getTime())) return null;
  const dayDiff = getDaysSinceVisit(lastVisit);
  if (dayDiff === 0) return t("landing_last_visit_today");
  if (dayDiff === 1) return t("landing_last_visit_yesterday");
  if (dayDiff != null && dayDiff < 7) return t("landing_last_visit_days_ago", { count: dayDiff });
  if (dayDiff != null && dayDiff < 30) {
    const weeks = Math.max(1, Math.floor(dayDiff / 7));
    return t("landing_last_visit_weeks_ago", { count: weeks });
  }
  return date.toLocaleDateString(locale || undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function resolveUserBelt(user) {
  const belt = user?.belt ?? user?.beltColor ?? user?.belt_color;
  if (!belt || typeof belt !== "string") return null;
  return belt;
}

function getBeltSectionDividerSx(beltValue) {
  const accent = beltValue ? getBeltAccentColor(beltValue) : null;
  if (!accent) return {};
  return { borderBottom: `5px solid ${accent}` };
}

/** Role chips aligned with UserAdmin: parent only, student only, or both. */
function getMemberRoleChips(user, t, { onDarkHero = false, isDarkMode = false } = {}) {
  const isParent = user.isParent === true;
  const isStudent = user.isStudent === true;
  const isChildAccount =
    user.hasParent === true ||
    user.parentID != null ||
    user.parentID2 != null;

  const mm = getMm(isDarkMode);
  const baseChipSx = {
    fontWeight: 600,
    fontSize: "0.7rem",
    height: 22,
    fontFamily: mm.fontBody,
    color: onDarkHero ? "#fff" : undefined,
  };

  const studentChipSx = onDarkHero
    ? { ...baseChipSx, backgroundColor: "rgba(255,255,255,0.18)", color: "#fff" }
    : {
        ...baseChipSx,
        backgroundColor: mm.primaryContainer,
        color: mm.onPrimaryContainer,
      };

  const chips = [];

  if (isParent) {
    chips.push({
      key: "parent",
      label: t("parent"),
      sx: onDarkHero
        ? { ...baseChipSx, backgroundColor: "rgba(157, 238, 237, 0.28)", color: "#fff" }
        : {
            ...baseChipSx,
            backgroundColor: mm.secondaryContainer,
            color: mm.onSecondaryContainer,
          },
    });
  }

  if (isStudent) {
    chips.push({
      key: "student",
      label: t("student"),
      sx: studentChipSx,
    });
  }

  // Child accounts may only have parent linkage flags, not isStudent
  if (!isParent && !isStudent && isChildAccount) {
    chips.push({
      key: "student",
      label: t("student"),
      sx: studentChipSx,
    });
  }

  // Student-only accounts when isStudent flag is missing on older records
  if (chips.length === 0 && !isParent) {
    chips.push({
      key: "student",
      label: t("student"),
      sx: studentChipSx,
    });
  }

  return chips;
}

function MemberRoleChips({ user, t, onDarkHero = false, isDarkMode = false }) {
  const chips = getMemberRoleChips(user, t, { onDarkHero, isDarkMode });
  return (
    <>
      {chips.map((chip) => (
        <Chip key={chip.key} label={chip.label} size="small" sx={chip.sx} />
      ))}
    </>
  );
}

function LandingStatTile({
  label,
  value,
  icon,
  isDarkMode,
  accentColor,
  children,
  wide = false,
  fillHeight = false,
  tilePadding = 1.25,
  isMobile = false,
  horizontal = false,
}) {
  const mm = getMm(isDarkMode);
  return (
    <Box
      sx={{
        p: isMobile ? Math.min(tilePadding, 1.25) : tilePadding,
        borderRadius: mm.radiusSm,
        textAlign: horizontal ? "left" : "center",
        backgroundColor: isDarkMode ? "rgba(50, 48, 54, 0.72)" : "rgba(255, 255, 255, 0.55)",
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : mm.outlineVariant}`,
        height: fillHeight && !horizontal ? "100%" : "auto",
        width: "100%",
        display: "flex",
        flexDirection: horizontal ? "row" : "column",
        alignItems: horizontal ? "center" : "center",
        justifyContent: horizontal ? "space-between" : "center",
        gap: horizontal ? 1 : 0.5,
        minHeight: horizontal ? "auto" : isMobile ? 72 : 88,
        py: horizontal ? { xs: 1.25, sm: 1.5 } : undefined,
        fontFamily: mm.fontBody,
      }}
    >
      {horizontal ? (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
            {icon && (
              <Box sx={{ color: accentColor || (getMm(isDarkMode).onSurfaceVariant), display: "flex", flexShrink: 0 }}>
                {icon}
              </Box>
            )}
            {label && (
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: getMm(isDarkMode).onSurfaceVariant,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.25rem",
              color: accentColor || (getMm(isDarkMode).onSurface),
              lineHeight: 1.2,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              flexShrink: 0,
            }}
          >
            {value ?? "—"}
          </Typography>
        </>
      ) : (
        <>
      {icon && (
        <Box sx={{ color: accentColor || (getMm(isDarkMode).onSurfaceVariant), display: "flex" }}>
          {icon}
        </Box>
      )}
      {children ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: fillHeight ? 1 : "unset",
            width: "100%",
            minHeight: fillHeight ? 0 : "unset",
          }}
        >
          {children}
        </Box>
      ) : (
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: isMobile ? "1rem" : "1.125rem",
            color: accentColor || (getMm(isDarkMode).onSurface),
            lineHeight: 1.2,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {value ?? "—"}
        </Typography>
      )}
      {label && (
      <Typography
        sx={{
          fontSize: isMobile ? "0.625rem" : "0.6875rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: getMm(isDarkMode).onSurfaceVariant,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          lineHeight: 1.3,
          px: 0.25,
        }}
      >
        {label}
      </Typography>
      )}
        </>
      )}
    </Box>
  );
}

function LandingMemberCard({
  user,
  stats,
  variant = "compact",
  isActive = false,
  onClick,
  unreadCount = 0,
  isPendingApproval = false,
  imageRefreshKey,
  isDarkMode,
  t,
  isMobile = false,
  locale,
}) {
  const isHero = variant === "hero";
  const beltValue = resolveUserBelt(user);
  const totalVisits = stats?.totalVisits;
  const monthVisits = stats?.monthVisits;
  const lastVisit = stats?.lastVisit;
  const relativeLastVisit = formatRelativeLastVisit(lastVisit, t, locale);
  const activityCopy = getActivityCopy(lastVisit, t);
  const avatarSize = isMobile ? 52 : 64;
  const beltSectionDividerSx = getBeltSectionDividerSx(beltValue);
  const mm = getMm(isDarkMode);
  const clickable = Boolean(onClick) && !isPendingApproval;
  const visitsTint = getLandingKpiTint(isDarkMode, "primary");
  const monthTint = getLandingKpiTint(isDarkMode, "success");

  const cardSx = isHero
    ? {
        width: "100%",
        maxWidth: 450,
        mx: "auto",
        borderRadius: mm.radiusMd,
        overflow: "hidden",
        fontFamily: mm.fontBody,
        ...getGlassSurfaceSx(isDarkMode, { elevated: true }),
      }
    : {
        borderRadius: mm.radiusMd,
        fontFamily: mm.fontBody,
        opacity: isPendingApproval ? 0.85 : 1,
        ...getInteractiveCardSx(isDarkMode, { clickable }),
        ...(isActive
          ? { border: `2px solid ${mm.secondary}` }
          : {}),
      };

  const handleCardKeyDown = (event) => {
    if (!clickable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      sx={cardSx}
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={handleCardKeyDown}
    >
      <CardContent sx={{ p: isHero ? { xs: 1.5, sm: 2.5 } : { xs: 1.25, sm: 2 }, "&:last-child": { pb: isHero ? { xs: 1.5, sm: 2.5 } : { xs: 1.25, sm: 2 } } }}>
        {!isHero && (
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.25, sm: 1.5 },
              alignItems: "flex-start",
              mb: { xs: 1.25, sm: 1.5 },
              ...(beltValue ? { pb: { xs: 1.25, sm: 1.5 }, ...beltSectionDividerSx } : {}),
            }}
          >
            <Badge
              badgeContent={isPendingApproval ? 0 : unreadCount}
              color="error"
              invisible={isPendingApproval || unreadCount === 0}
              sx={{ "& .MuiBadge-badge": { fontSize: "0.7rem", minWidth: 18, height: 18 } }}
            >
              <SupabaseImageAvatar
                user={user}
                size={avatarSize}
                refreshKey={imageRefreshKey}
                sx={{
                  border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                  boxShadow: "none",
                }}
              />
            </Badge>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: getMm(isDarkMode).onSurface,
                  fontSize: "1rem",
                  lineHeight: 1.3,
                  mb: 0.5,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {user.name || t("no_name_available")}
              </Typography>
              {isPendingApproval ? (
                <Chip
                  label={t("pending_parent_approval")}
                  size="small"
                  sx={{
                    backgroundColor: mm.tertiaryContainer,
                    color: mm.onTertiary,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 22,
                    fontFamily: mm.fontBody,
                  }}
                />
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                  <MemberRoleChips user={user} t={t} isDarkMode={isDarkMode} />
                  {user.studentID && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.75rem",
                        color: getMm(isDarkMode).onSurfaceVariant,
                        alignSelf: "center",
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                      }}
                    >
                      #{user.studentID}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Grid container spacing={{ xs: 1, sm: 1.25 }} alignItems="stretch">
          <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
            <LandingStatTile
              label={t("total_visits")}
              value={totalVisits != null ? totalVisits : "—"}
              icon={<EventAvailableIcon sx={{ fontSize: isMobile ? 16 : 18 }} />}
              isDarkMode={isDarkMode}
              accentColor={visitsTint.fg}
              fillHeight={!isMobile}
              isMobile={isMobile}
              horizontal={isMobile}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
            <LandingStatTile
              label={t("landing_visits_this_month")}
              value={monthVisits != null ? monthVisits : "—"}
              icon={<AccessTimeIcon sx={{ fontSize: isMobile ? 16 : 18 }} />}
              isDarkMode={isDarkMode}
              accentColor={monthTint.fg}
              fillHeight={!isMobile}
              isMobile={isMobile}
              horizontal={isMobile}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.25, borderColor: mm.outlineVariant }} />
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: mm.onSurface,
            textAlign: "center",
            fontFamily: mm.fontBody,
            lineHeight: 1.35,
          }}
        >
          {activityCopy.title}
        </Typography>
        {(relativeLastVisit || lastVisit) && (
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: mm.onSurfaceVariant,
              textAlign: "center",
              fontFamily: mm.fontBody,
              mt: 0.35,
            }}
          >
            {t("last_visit")}:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: mm.onSurface }}>
              {relativeLastVisit || new Date(lastVisit).toLocaleDateString(locale || undefined)}
            </Box>
          </Typography>
        )}
        {activityCopy.hint && (
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: mm.onSurfaceVariant,
              textAlign: "center",
              fontFamily: mm.fontBody,
              mt: 0.35,
              lineHeight: 1.4,
            }}
          >
            {activityCopy.hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function LandingQuickActions({
  activeUser,
  t,
  setSelectedPage,
  onAddFamily,
  align = "center",
  isMobile = false,
  cashPending = false,
}) {
  const { outlineButtonSx, primaryButtonSx } = useSystemButtonStyles();
  const { isDarkMode } = useTheme();

  if (!activeUser) return null;

  const showCoachButton =
    !cashPending &&
    (activeUser?.reviewed || activeUser?.rejected) &&
    activeUser?.isCoach === true;
  const showAddFamily = !cashPending && activeUser?.isParent === true;

  const actions = [
    {
      key: "membership",
      label: t("membership_card") || "Membership Card",
      icon: QrCodeIcon,
      onClick: () => setSelectedPage("DigitalCardPage"),
      primary: true,
    },
    showCoachButton && {
      key: "coach",
      label: t("coach_time_registration"),
      icon: AccessTimeIcon,
      onClick: () => setSelectedPage("TimeRegistrationPage"),
    },
    showAddFamily && {
      key: "family",
      label: t("add_family_member"),
      icon: AddIcon,
      onClick: onAddFamily,
    },
  ].filter(Boolean);

  const stackFullWidth = align === "center";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: stackFullWidth
          ? "1fr"
          : {
              xs: actions.length > 1 ? "repeat(2, minmax(0, 1fr))" : "1fr",
              sm: actions.length > 1 ? `repeat(${Math.min(actions.length, 3)}, minmax(0, 1fr))` : "1fr",
            },
        gap: 1.25,
        width: "100%",
        minWidth: 0,
        justifyItems: "stretch",
      }}
    >
      {actions.map((action, index) => {
        const Icon = action.icon;
        const spanFullWidth =
          stackFullWidth || (isMobile && actions.length % 2 === 1 && index === actions.length - 1);
        return (
          <Button
            key={action.key}
            variant={action.primary ? "contained" : "outlined"}
            startIcon={<Icon sx={{ fontSize: 20 }} />}
            onClick={action.onClick}
            sx={{
              ...(action.primary ? primaryButtonSx : outlineButtonSx),
              ...getFocusVisibleSx(isDarkMode),
              ...getMotionTransition("background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease"),
              gridColumn: spanFullWidth ? "1 / -1" : undefined,
              width: "100%",
              minHeight: 44,
              py: 1.1,
              px: 1.5,
              fontSize: "0.875rem",
              lineHeight: 1.3,
              justifyContent: "center",
              textAlign: "center",
              whiteSpace: "normal",
              cursor: "pointer",
            }}
          >
            {action.label}
          </Button>
        );
      })}
    </Box>
  );
}

const LandingPage = ({
  selectedPage,
  setSelectedPage,
  userInfo,
  setUserInfo,
  refreshNavbar,
  setIsLoading: setGlobalLoading,
  onAccessBlockChange,
}) => {
  const { user } = useContext(SupabaseUserContext);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (setGlobalLoading && selectedPage === "LandingPage") setGlobalLoading(isLoading);
  }, [isLoading, selectedPage, setGlobalLoading]);
  const [activeUser, setActiveUser] = useState(null);
  const activeMemberSportInfo = useMemo(
    () => (activeUser ? [activeUser] : []),
    [activeUser]
  );
  const { sport: activeMemberSport, loaded: activeMemberSportLoaded } =
    useActiveMemberSport(activeMemberSportInfo);
  const showBeltOnMemberCard = memberShowsBeltRankMenu(activeMemberSport, {
    loaded: activeMemberSportLoaded,
  });
  const [localUserInfo, setLocalUserInfo] = useState([]);
  const [emailTrigger, setEmailTrigger] = useState(localStorage.getItem("email"));
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const { apiMe } = useClubScope();
  const cashPending = isCashPaymentPending(apiMe, userInfo);
  const {
    primaryButtonSx,
    cancelButtonSx,
    outlineButtonSx,
    secondaryButtonSx,
    destructiveButtonSx,
    destructiveOutlineButtonSx,
    greenSmileyButtonSx,
    redSmileyButtonSx,
    iconActionSx,
    iconActionTableSx,
    iconActionLargeSx,
    toolbarButtonSx,
    dialogPrimarySx,
    dialogCancelSx,
    dialogFormPrimarySx,
    dialogFormCancelSx,
    dialogFormDestructiveSx,
    warningButtonSx,
    buttonSx,
  } = useSystemButtonStyles();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [showSwitchOverlay, setShowSwitchOverlay] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [accessBlock, setAccessBlock] = useState(null);

  const clearSessionUserState = useCallback(async () => {
    setLocalUserInfo([]);
    setActiveUser(null);
    if (setUserInfo) setUserInfo([]);
    try {
      await localforage.removeItem("userInfo");
      await localforage.removeItem("activeUser");
    } catch {
      /* ignore */
    }
  }, [setUserInfo]);
  
  // Cache-busting key for images - regenerates every time the page loads or data refreshes
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Track the actual logged-in user's email from Supabase (authoritative source)
  const loggedInUserEmail = useMemo(() => {
    const emailFromSupabase = user?._profile?.data?.email || user?.email;
    const emailFromStorage = localStorage.getItem("email");
    // Prefer Supabase email as it's the source of truth for authentication
    return emailFromSupabase || emailFromStorage || '';
  }, [user]);





  useEffect(() => {
    if (selectedPage !== "LandingPage") {
      onAccessBlockChange?.(null);
      return;
    }
    if (isLoading || !hasAttemptedLoad) {
      onAccessBlockChange?.(null);
      return;
    }
    onAccessBlockChange?.(accessBlock);
  }, [accessBlock, selectedPage, isLoading, hasAttemptedLoad, onAccessBlockChange]);

  const checkEmailChange = useCallback(() => {
    const currentEmail = localStorage.getItem("email");
    if (currentEmail !== emailTrigger) {
      setEmailTrigger(currentEmail);
    }
  }, [emailTrigger]);

  const fetchMainUserByEmailMemo = useCallback(fetchMainUserByEmail, []);
  const fetchChildUserByEmailMemo = useCallback(fetchChildUserByEmail, []);


  useEffect(() => {
    // Increased interval to reduce unnecessary checks
    const interval = setInterval(checkEmailChange, 2000);
    return () => clearInterval(interval);
  }, [checkEmailChange]);

  useEffect(() => {
    if (selectedPage !== "LandingPage") {
      setIsLoading(false);
      return;
    }

    // Check if user is logged in - check both localStorage and Supabase context
    const emailFromStorage = localStorage.getItem("email");
    const emailFromSupabase = user?._profile?.data?.email || user?.email;
    const currentEmail = emailFromStorage || emailFromSupabase;
    
    if (!currentEmail || currentEmail.trim() === "") {
      setActiveUser(null);
      setLocalUserInfo([]);
      setIsLoading(false);
      setHasAttemptedLoad(true);
      const hasAuthSession = Boolean(user) || tokenManager.isTokenValid();
      const blockType = hasAuthSession
        ? LANDING_ACCESS.SESSION_EXPIRED
        : LANDING_ACCESS.NOT_LOGGED_IN;
      setAccessBlock({ type: blockType });
      if (blockType === LANDING_ACCESS.SESSION_EXPIRED) {
        void clearSessionUserState();
      }
      return;
    }

    if (emailFromSupabase && !emailFromStorage) {
      localStorage.setItem("email", emailFromSupabase);
    }

    if (!tokenManager.isTokenValid()) {
      setIsLoading(false);
      setHasAttemptedLoad(true);
      setAccessBlock({ type: LANDING_ACCESS.SESSION_EXPIRED });
      void clearSessionUserState();
      return;
    }

    setIsLoading(true);
    setAccessBlock(null);
    setHasAttemptedLoad(false);

    const loadData = async () => {
      try {
        const storedEmail = localStorage.getItem("email");
        if (!storedEmail) {
          setAccessBlock({ type: LANDING_ACCESS.NOT_LOGGED_IN });
          setIsLoading(false);
          setHasAttemptedLoad(true);
          return;
        }

        const [mainUsers, childUsers] = await Promise.allSettled([
          fetchMainUserByEmailMemo(storedEmail),
          fetchChildUserByEmailMemo(storedEmail),
        ]);

        const mainUsersData =
          mainUsers.status === "fulfilled"
            ? Array.isArray(mainUsers.value)
              ? mainUsers.value
              : mainUsers.value
                ? [mainUsers.value]
                : []
            : [];
        const childUsersData =
          childUsers.status === "fulfilled"
            ? Array.isArray(childUsers.value)
              ? childUsers.value
              : childUsers.value
                ? [childUsers.value]
                : []
            : [];

        const mergedUsers = [
          ...mainUsersData,
          ...childUsersData.filter(
            (c) => !mainUsersData.some((p) => p.studentID === c.studentID)
          ),
        ];

        const hasAuthSession = Boolean(user) || tokenManager.isTokenValid();
        const mainFailure = getFetchFailureInfo(mainUsers, { hasAuthSession });
        const childFailure = getFetchFailureInfo(childUsers, { hasAuthSession });

        const resolvedBlock = resolveAccessBlockAfterFetch({
          mainFailure,
          childFailure,
          mergedUsersLength: mergedUsers.length,
          hasAuthSession,
        });

        if (resolvedBlock?.type === LANDING_ACCESS.SESSION_EXPIRED) {
          setAccessBlock(resolvedBlock);
          await clearSessionUserState();
          setIsLoading(false);
          setHasAttemptedLoad(true);
          return;
        }

        if (resolvedBlock?.type === LANDING_ACCESS.UNDER_REVIEW) {
          setAccessBlock(resolvedBlock);
          setLocalUserInfo([]);
          setActiveUser(null);
          setIsLoading(false);
          setHasAttemptedLoad(true);
          return;
        }

        if (resolvedBlock?.type === LANDING_ACCESS.CONNECTION_ERROR) {
          setAccessBlock(resolvedBlock);
          setLocalUserInfo([]);
          setActiveUser(null);
          setIsLoading(false);
          setHasAttemptedLoad(true);
          return;
        }

        if (resolvedBlock?.type === LANDING_ACCESS.ACCOUNT_NOT_FOUND) {
          setAccessBlock(resolvedBlock);
          setLocalUserInfo([]);
          setActiveUser(null);
          await localforage.removeItem("userInfo");
          await localforage.removeItem("activeUser");
          setIsLoading(false);
          setHasAttemptedLoad(true);
          return;
        }

        setAccessBlock(null);

        let loadedInfo = mergedUsers;

        // Try to restore stored activeUser to preserve user selection
        // SECURITY: Verify stored user still belongs to logged-in user
        const storedActiveUser = await localforage.getItem("activeUser");
        let loadedActive = null;

        if (storedActiveUser && loggedInUserEmail) {
          const foundUser = loadedInfo.find(u => u.studentID === storedActiveUser.studentID);
          if (foundUser) {
            // SECURITY CHECK: Verify the stored user belongs to the logged-in user
            const belongsToLoggedInUser = 
              // If it's a parent, email must match
              (foundUser.isParent && foundUser.email && 
               foundUser.email.toLowerCase() === loggedInUserEmail.toLowerCase()) ||
              // If it's a child, must belong to logged-in parent AND connection must be approved
              (!foundUser.isParent && (() => {
                const loggedInParent = loadedInfo.find(u => 
                  u.isParent === true && 
                  u.email && 
                  u.email.toLowerCase() === loggedInUserEmail.toLowerCase()
                );
                const parentId = String(loggedInParent?.studentID ?? loggedInParent?.id ?? '');
                if (!loggedInParent) return false;
                const isParent1 = foundUser.parentID != null && String(foundUser.parentID) === parentId;
                const isParent2 = foundUser.parentID2 != null && String(foundUser.parentID2) === parentId;
                return (isParent1 && foundUser.parentIDApproved === true) ||
                       (isParent2 && foundUser.parentID2Approved === true);
              })());
            
            if (belongsToLoggedInUser) {
              // Use stored activeUser but merge with fresh data
              loadedActive = { ...storedActiveUser, ...foundUser };
            }
            // If stored user doesn't belong to logged-in user, ignore it (loadedActive stays null)
          }
        }

        // Default to parent that matches logged-in email if no stored activeUser
        if (!loadedActive && loadedInfo.length > 0) {
          // First, try to find the parent account that matches the logged-in email
          const loggedInParent = loadedInfo.find(u => 
            u.isParent === true && 
            u.email && 
            u.email.toLowerCase() === loggedInUserEmail.toLowerCase()
          );
          
          // If not found, fall back to any parent
          if (loggedInParent) {
            loadedActive = loggedInParent;
          } else {
            const parentUser = loadedInfo.find(u => u.isParent === true);
            loadedActive = parentUser || loadedInfo[0];
          }
        }

        // Fetch family members
        let fetchFamilyForID = loadedActive?.studentID;
        if (loadedActive) {
          if (loadedActive.hasParent) {
            fetchFamilyForID = loadedActive.parentID || loadedActive.parentID2 || loadedActive.studentID;
          } else if (loadedActive.isParent) {
            fetchFamilyForID = loadedActive.studentID;
          }
        }

        if (fetchFamilyForID) {
          try {
            const response = await fetchFamilyByID(fetchFamilyForID);
            let familyData = Array.isArray(response.data)
              ? response.data
              : Array.isArray(response)
                ? response
                : [];
            const mergedUsersWithFamily = [
              ...loadedInfo,
              ...familyData.filter(
                newUser => !loadedInfo.some(e => e.studentID === newUser.studentID)
              ),
            ];
            loadedInfo = mergedUsersWithFamily;
          } catch (err) {
            // Failed to fetch family members - continue without them
          }
        }

        // Save to cache and update state
        await localforage.setItem("userInfo", loadedInfo);
        if (loadedActive) {
          await localforage.setItem("activeUser", loadedActive);
        }

        setLocalUserInfo(loadedInfo);
        setActiveUser(loadedActive || null);
        
        // Sync activeUser to parent component
        if (loadedActive && setUserInfo) {
          setUserInfo([loadedActive]);
        }
        
        setIsLoading(false);
        setHasAttemptedLoad(true);
      } catch (error) {
        const hasAuthSession = Boolean(user) || tokenManager.isTokenValid();
        const resolvedBlock = resolveAccessBlockFromLoadError(error, { hasAuthSession });

        if (resolvedBlock.type === LANDING_ACCESS.SESSION_EXPIRED) {
          setAccessBlock(resolvedBlock);
          await clearSessionUserState();
          setIsLoading(false);
          setHasAttemptedLoad(true);
          return;
        }

        setAccessBlock(resolvedBlock);
        setLocalUserInfo([]);
        setActiveUser(null);
        await localforage.removeItem("userInfo");
        await localforage.removeItem("activeUser");
        setIsLoading(false);
        setHasAttemptedLoad(true);
      }
    };

    loadData();
  }, [
    selectedPage,
    emailTrigger,
    fetchMainUserByEmailMemo,
    fetchChildUserByEmailMemo,
    loggedInUserEmail,
    refreshTrigger,
    setUserInfo,
    user,
    clearSessionUserState,
  ]);

  // Sync activeUser changes to parent component's userInfo prop
  // This ensures other pages (like Admin Settings) see the correct selected user
  useEffect(() => {
    if (activeUser && setUserInfo) {
      setUserInfo([activeUser]);
    }
  }, [activeUser, setUserInfo]);
  
  // Regenerate image refresh key when page is shown to ensure fresh images
  useEffect(() => {
    if (selectedPage === "LandingPage") {
      setImageRefreshKey(Date.now());
    }
  }, [selectedPage]);
  
  // Also regenerate refresh key when activeUser changes (e.g., after switching accounts or data refresh)
  useEffect(() => {
    if (selectedPage === "LandingPage" && activeUser) {
      setImageRefreshKey(Date.now());
    }
  }, [selectedPage, activeUser]);

  const addNewUsers = useCallback(() => setSelectedPage("ProfileManageFamily"), [setSelectedPage]);

  const handleSwitchUser = useCallback(async (user) => {
    // CRITICAL SECURITY CHECK: Verify user can switch to this account
    const currentLoggedInEmail = loggedInUserEmail || localStorage.getItem("email");
    
    if (!currentLoggedInEmail) {
      console.error('Cannot switch user: No logged-in email found');
      setShowSwitchOverlay(false);
      return;
    }
    
    // Check if the user being switched to belongs to the logged-in user
    const canSwitchToThisUser = 
      // If it's a parent account, email must match logged-in email
      (user.isParent && user.email && user.email.toLowerCase() === currentLoggedInEmail.toLowerCase()) ||
      // If it's a child account, it must have parentID or parentID2 matching logged-in parent AND be approved
      (!user.isParent && (() => {
        const loggedInParent = localUserInfo.find(u => 
          u.isParent === true && 
          u.email && 
          u.email.toLowerCase() === currentLoggedInEmail.toLowerCase()
        );
        if (!loggedInParent) return false;
        const parentId = String(loggedInParent.studentID ?? loggedInParent.id ?? '');
        const isParent1 = user.parentID != null && String(user.parentID) === parentId;
        const isParent2 = user.parentID2 != null && String(user.parentID2) === parentId;
        return (isParent1 && user.parentIDApproved === true) ||
               (isParent2 && user.parentID2Approved === true);
      })());
    
    if (!canSwitchToThisUser) {
      console.error('Cannot switch to this account: Does not belong to logged-in user');
      alert('You can only switch to accounts that belong to you.');
      return;
    }
    
    setShowSwitchOverlay(true);
    try {
      const response = await fetchUserByStudentID(user.studentID);
      if (!response) return;

      // Process image data if present (same logic as OptimizedAvatar)
      if (response.image && typeof response.image === 'string') {
        // Handle MongoDB Buffer object format (base64-encoded JSON)
        if (response.image.startsWith('eyJ0eXBlIjoiQnVmZmVyIiwiZGF0YSI6')) {
          try {
            const jsonString = atob(response.image);
            const bufferObj = JSON.parse(jsonString);
            
            if (bufferObj.type === 'Buffer' && Array.isArray(bufferObj.data)) {
              // Convert the array of bytes to a Uint8Array
              const uint8Array = new Uint8Array(bufferObj.data);
              
              // Convert to base64 using chunked processing for large images
              let binaryString = '';
              const chunkSize = 8192;
              
              for (let i = 0; i < uint8Array.length; i += chunkSize) {
                const chunk = uint8Array.slice(i, i + chunkSize);
                binaryString += String.fromCharCode.apply(null, chunk);
              }
              
              const base64String = btoa(binaryString);
              response.image = base64String;
            }
          } catch (error) {
            // Error processing image - continue without processing
          }
        }
      }

      // Update the user in the existing userInfo array with processed image
      let currentUserInfo = (await localforage.getItem("userInfo")) || [];
      const userIndex = currentUserInfo.findIndex(u => u.studentID === response.studentID);
      if (userIndex !== -1) {
        // Update existing user with processed image data
        currentUserInfo[userIndex] = { ...currentUserInfo[userIndex], ...response };
        await localforage.setItem("userInfo", currentUserInfo);
      } else {
        // Add new user if not found
        currentUserInfo.push(response);
        await localforage.setItem("userInfo", currentUserInfo);
      }

      setActiveUser(response);
      await localforage.setItem("activeUser", response);

      // Sync to parent component immediately
      if (setUserInfo) {
        setUserInfo([response]);
      }

      // CRITICAL: DO NOT change the logged-in email
      // Only update display-related localStorage items
      // Keep the logged-in email unchanged to maintain security
      localStorage.setItem("club", response.clubname || "");
      localStorage.setItem("name", response.name || "");
      // Note: We intentionally do NOT update localStorage email here
      // The email should remain as the logged-in user's email

      setShowSwitchOverlay(false);
      setIsLoading(false);
      window.location.reload();
    } catch (error) {
      setIsLoading(false);
      setShowSwitchOverlay(false);
    }
  }, [loggedInUserEmail, localUserInfo, setUserInfo]);

  // Memoized parent anchor ID calculation
  // CRITICAL: Always use the logged-in user's parent account, not just any parent
  const parentAnchorID = useMemo(() => {
    if (!activeUser || !loggedInUserEmail) return null;
    
    // If active user is a parent and their email matches logged-in email, use their ID
    if (activeUser.isParent && activeUser.email && 
        activeUser.email.toLowerCase() === loggedInUserEmail.toLowerCase()) {
      return activeUser.studentID;
    }
    
    // If active user is a child, find which parent ID belongs to the logged-in user
    if (activeUser.hasParent) {
      // Find the parent account that matches the logged-in email
      const loggedInParent = localUserInfo.find(u => 
        u.isParent === true && 
        u.email && 
        u.email.toLowerCase() === loggedInUserEmail.toLowerCase()
      );
      
      if (loggedInParent) {
        const parentId = String(loggedInParent.studentID ?? loggedInParent.id ?? '');
        if (activeUser.parentID != null && String(activeUser.parentID) === parentId) {
          return activeUser.parentID;
        }
        if (activeUser.parentID2 != null && String(activeUser.parentID2) === parentId) {
          return activeUser.parentID2;
        }
      }
      
      // Fallback: use parentID if it exists, but this shouldn't happen in normal flow
      return activeUser.parentID || activeUser.parentID2 || activeUser.studentID;
    }
    
    // Fallback
    return activeUser.studentID;
  }, [activeUser, loggedInUserEmail, localUserInfo]);

  // Unread message counts per child (for parent to see badges on their children's cards)
  const [unreadCountsByStudent, setUnreadCountsByStudent] = useState({});
  const [memberStatsMap, setMemberStatsMap] = useState({});

  // Memoized associated accounts filtering
  // CRITICAL: Only show accounts that belong to the logged-in user
  const associatedAccounts = useMemo(() => {
    if (!loggedInUserEmail || !parentAnchorID) return [];
    
    return localUserInfo.filter(user => {
      // Don't show the active user
      if (user.studentID === activeUser?.studentID) return false;
      
      // CRITICAL: Only show parent accounts if they match the logged-in email
      if (user.isParent) {
        return user.email && user.email.toLowerCase() === loggedInUserEmail.toLowerCase();
      }
      
      // For child accounts: show if they belong to the logged-in parent (approved and pending both shown)
      const isParent1 = user.parentID === parentAnchorID || String(user.parentID) === String(parentAnchorID);
      const isParent2 = user.parentID2 === parentAnchorID || String(user.parentID2) === String(parentAnchorID);
      return (isParent1 || isParent2);
    });
  }, [localUserInfo, activeUser?.studentID, parentAnchorID, loggedInUserEmail]);

  const loggedInAccount = useMemo(
    () => findLoggedInAccount(localUserInfo, loggedInUserEmail),
    [localUserInfo, loggedInUserEmail]
  );

  const familyAccounts = useMemo(() => {
    if (!loggedInAccount) return loggedInUserEmail && activeUser ? [activeUser] : [];
    const anchorId = loggedInAccount.studentID;
    const accounts = localUserInfo.filter((user) => {
      if (user.studentID === anchorId) return true;
      if (user.isParent) return false;
      const isParent1 =
        user.parentID != null && String(user.parentID) === String(anchorId);
      const isParent2 =
        user.parentID2 != null && String(user.parentID2) === String(anchorId);
      return isParent1 || isParent2;
    });
    return accounts.sort((a, b) => {
      if (a.studentID === anchorId) return -1;
      if (b.studentID === anchorId) return 1;
      return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      });
    });
  }, [localUserInfo, loggedInAccount, loggedInUserEmail, activeUser]);

  const showStaffDashboard = useMemo(
    () => shouldShowStaffDashboard(activeUser, loggedInUserEmail, localUserInfo),
    [activeUser, loggedInUserEmail, localUserInfo]
  );

  const showFamilySwitcher = showStaffDashboard && familyAccounts.length > 1;
  const staffOverviewForLoggedIn = useMemo(
    () => userShowsStaffDashboard(loggedInAccount),
    [loggedInAccount]
  );

  // Fetch unread message counts for each associated account (so parent can see badges on children's cards)
  useEffect(() => {
    if (!associatedAccounts.length || selectedPage !== "LandingPage") return;
    let cancelled = false;
    const fetchCounts = async () => {
      const counts = {};
      await Promise.all(
        associatedAccounts.map(async (acc) => {
          if (cancelled) return;
          try {
            const clubID = acc.clubID ?? acc.clubId ?? null;
            const count = await fetchUnreadMessageCount(acc.studentID, clubID);
            if (!cancelled) counts[acc.studentID] = count ?? 0;
          } catch {
            if (!cancelled) counts[acc.studentID] = 0;
          }
        })
      );
      if (!cancelled) setUnreadCountsByStudent(counts);
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000); // Refresh every 60s
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [associatedAccounts, selectedPage]);

  const statsStudentIds = useMemo(() => {
    const ids = [];
    if (activeUser?.studentID) ids.push(activeUser.studentID);
    associatedAccounts.forEach((u) => {
      if (u.studentID && !ids.includes(u.studentID)) ids.push(u.studentID);
    });
    return ids;
  }, [activeUser?.studentID, associatedAccounts]);

  useEffect(() => {
    if (selectedPage !== "LandingPage" || statsStudentIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const { startDate, endDate } = getCurrentMonthDateRange();
      const results = await Promise.allSettled(
        statsStudentIds.map(async (id) => {
          const [allTimeData, monthData] = await Promise.all([
            fetchUserAttendanceStats(id),
            fetchUserAttendanceStats(id, startDate, endDate),
          ]);
          const parsed = parseMemberAttendanceStats(allTimeData);
          const monthParsed = parseMemberAttendanceStats(monthData);
          return [
            id,
            {
              ...parsed,
              monthVisits: monthParsed.totalVisits,
            },
          ];
        })
      );
      if (cancelled) return;
      const map = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          const [id, stats] = r.value;
          map[id] = stats;
        }
      });
      setMemberStatsMap((prev) => ({ ...prev, ...map }));
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPage, statsStudentIds, refreshTrigger]);

  // Memoized review status check - Show "Under Review" when rejected is true OR reviewed is false
  // CRITICAL: Only show with FRESH, VALIDATED user data. Never use stale cached data.
  // EXCEPTION: If user is rejected but has allowEdit = true, don't show "Under Review" - let them fix and resubmit
  const isUnderReview = useMemo(() => {
    // CRITICAL CHECK 1: Must have a valid email in localStorage (user is actually logged in)
    const storedEmail = localStorage.getItem("email");
    if (!storedEmail || storedEmail.trim() === '') {
      return false; // No email = user logged out, don't show anything
    }
    
    // CRITICAL CHECK 2: Never show during loading - always wait for data to be fully ready
    if (isLoading || !hasAttemptedLoad) {
      return false;
    }
    
    // CRITICAL CHECK 3: Must have activeUser with valid studentID
    // Don't check localUserInfo - only use activeUser which is the most current data
    if (!activeUser || !activeUser.studentID) {
      return false; // No active user = don't show review message
    }
    
    // CRITICAL CHECK 4: Verify the activeUser's email matches the logged-in email
    // This prevents showing stale data from a previous user session
    if (activeUser.email && activeUser.email.trim() !== '' && activeUser.email !== storedEmail) {
      return false; // Email mismatch = stale data, don't show
    }
    
    // CRITICAL CHECK 4.5: Parent viewing child - don't show "under review"
    // When a parent switches to their child's account, the child may have reviewed=false.
    // The parent has access - don't block them from viewing their child's data.
    if (!activeUser.isParent && (activeUser.parentID != null || activeUser.parentID2 != null)) {
      const loggedInParent = localUserInfo.find(u =>
        u.isParent === true &&
        u.email &&
        u.email.toLowerCase().trim() === storedEmail.toLowerCase().trim()
      );
      if (loggedInParent) {
        const parentIdStr = String(loggedInParent.studentID ?? loggedInParent.id ?? '');
        const childParent1 = activeUser.parentID != null ? String(activeUser.parentID) : '';
        const childParent2 = activeUser.parentID2 != null ? String(activeUser.parentID2) : '';
        if (parentIdStr && (parentIdStr === childParent1 || parentIdStr === childParent2)) {
          return false; // Parent viewing their child - allow access
        }
      }
    }
    
    // CRITICAL CHECK 5: Exception for rejected users with allowEdit = true
    // If user is rejected but can edit, don't show "Under Review" - they can fix and resubmit
    if (activeUser.rejected === true && activeUser.allowEdit === true) {
      return false; // Let them see WarningCard and edit profile instead
    }

    // Prova-på trial: photo review in Approval Center, but full app access until reject
    if (activeUser.provaPaSilentPhotoReview === true && activeUser.rejected !== true) {
      return false;
    }
    
    // CRITICAL CHECK 6: Only now check the review status
    // Show "Under Review" if:
    // 1. rejected is true (and allowEdit is false), OR
    // 2. reviewed is false, undefined, or null (all mean not reviewed/not approved)
    const isRejected = activeUser.rejected === true;
    // Check if reviewed is false, undefined, or null (treat all as "not reviewed")
    // Only treat as "approved" if reviewed is explicitly true
    const isNotReviewed = activeUser.reviewed !== true;
    
    return isRejected || isNotReviewed;
  }, [activeUser, hasAttemptedLoad, isLoading, localUserInfo]);

  const statusCardType = useMemo(() => {
    if (isLoading || !hasAttemptedLoad) return null;
    if (isUnderReview) return { type: LANDING_ACCESS.UNDER_REVIEW };
    return accessBlock || null;
  }, [isLoading, hasAttemptedLoad, isUnderReview, accessBlock]);

  if (selectedPage !== "LandingPage") return null;

  return (
    <Container 
      maxWidth={showStaffDashboard ? "xl" : "md"}
      sx={{ 
        py: { xs: 2, sm: showStaffDashboard ? 3 : 4 },
        px: { xs: 1.25, sm: 2 },
        pb: { xs: '100px', sm: 10 },
        width: '100%',
        minWidth: 0,
        maxWidth: { xs: '100%', sm: showStaffDashboard ? 'xl' : 'md' },
        overflowX: 'hidden',
      }}
    >
      {/* Show loader while fetching user data - standardized PageLoader */}
      {isLoading ? (
        <PageLoader message={t("loading_text") || "Loading..."} />
      ) : statusCardType ? (
        <LandingStatusCard
          type={statusCardType.type}
          archived={Boolean(statusCardType.archived)}
          isDarkMode={isDarkMode}
          t={t}
          onRefresh={() => setRefreshTrigger((prev) => prev + 1)}
          outlineButtonSx={outlineButtonSx}
          primaryButtonSx={primaryButtonSx}
        />
      ) : (
        <Fade in={!isLoading} timeout={prefersReducedMotion ? 0 : 400}>
          <Box sx={{ minWidth: 0 }}>
            {/* Banned User Alert - Admin Dashboard Style */}
            {activeUser?.isBanned && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 4,
                  borderRadius: getMm(isDarkMode).radiusSm,
                  boxShadow: "none",
                  backgroundColor: isDarkMode ? getMm(isDarkMode).errorContainer : "#FEF2F2",
                  border: `2px solid ${getMm(isDarkMode).error}`,
                  fontFamily: getMm(isDarkMode).fontBody,
                  '& .MuiAlert-icon': { color: getMm(isDarkMode).error }
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: '1rem', fontFamily: "'Plus Jakarta Sans', sans-serif", color: getMm(isDarkMode).onSurface }}>
                  Your account has been banned. Please contact support for more information.
                </Typography>
              </Alert>
            )}

            {activeUser && activeUser.isAdmin !== true && (
              <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
                <LandingFlipMemberCard
                  user={activeUser}
                  t={t}
                  imageRefreshKey={imageRefreshKey}
                  prefersReducedMotion={prefersReducedMotion}
                  isDarkMode={isDarkMode}
                  showBeltRank={showBeltOnMemberCard}
                />
              </Box>
            )}

            {/* Family switcher — staff overview only (regular member view keeps original layout) */}
            {!isUnderReview && showFamilySwitcher && !cashPending && (
              <LandingFamilySwitcher
                accounts={familyAccounts}
                activeUser={activeUser}
                loggedInAccount={loggedInAccount}
                showStaffOverviewForLoggedIn={staffOverviewForLoggedIn}
                parentAnchorID={parentAnchorID}
                unreadCountsByStudent={unreadCountsByStudent}
                imageRefreshKey={imageRefreshKey}
                isDarkMode={isDarkMode}
                t={t}
                onSwitch={handleSwitchUser}
                isMobile={isMobile}
              />
            )}

            {/* Staff quick actions — top on mobile (before long dashboard) */}
            {!isUnderReview && showStaffDashboard && activeUser && (
              <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
                <LandingQuickActions
                  activeUser={activeUser}
                  t={t}
                  setSelectedPage={setSelectedPage}
                  onAddFamily={addNewUsers}
                  align="start"
                  isMobile={isMobile}
                  cashPending={cashPending}
                />
              </Box>
            )}

            {/* Staff daily dashboard (admin/coach on own account) */}
            {showStaffDashboard && (
              <StaffDailyDashboard
                isDarkMode={isDarkMode}
                userName={activeUser?.name}
                setSelectedPage={setSelectedPage}
                isAdmin={activeUser?.isAdmin === true}
                clubID={activeUser?.clubID ?? activeUser?.clubId}
              />
            )}

            {/* Active User Card — members and family-switched views */}
            {activeUser && !showStaffDashboard && (
              <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
                <LandingMemberCard
                  user={activeUser}
                  stats={memberStatsMap[activeUser.studentID]}
                  variant="hero"
                  isActive
                  imageRefreshKey={imageRefreshKey}
                  isDarkMode={isDarkMode}
                  t={t}
                  isMobile={isMobile}
                  locale={i18n.language}
                />
              </Box>
            )}

            {/* Membership Card + Coach Time — member view only (staff actions are above dashboard) */}
            {activeUser && !showStaffDashboard && (
              <Box sx={{ mb: { xs: 2.5, sm: 4 }, maxWidth: 450, mx: "auto", width: "100%" }}>
                <LandingQuickActions
                  activeUser={activeUser}
                  t={t}
                  setSelectedPage={setSelectedPage}
                  onAddFamily={addNewUsers}
                  align="center"
                  isMobile={isMobile}
                  cashPending={cashPending}
                />
              </Box>
            )}

            {/* Warning Card */}
            {activeUser?.allowEdit && !cashPending && <WarningCard setSelectedPage={setSelectedPage} />}

            {/* Normal content - only show if user is NOT under review */}
            {!isUnderReview && !cashPending && (
              <>
                {/* Legacy grid when switcher is not shown (single extra account edge case) */}
                {!showStaffDashboard && associatedAccounts.length > 0 && (
                  <Box sx={{ mb: { xs: 2.5, sm: 4 }, fontFamily: getMm(isDarkMode).fontBody }}>
                    <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                      <Typography 
                        sx={{ 
                          fontWeight: 500,
                          color: getMm(isDarkMode).onSurface,
                          mb: 0.75,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          fontFamily: getMm(isDarkMode).fontDisplay,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {t("other_accounts")}
                      </Typography>
                      <Typography 
                        sx={{ 
                          color: getMm(isDarkMode).onSurfaceVariant,
                          fontWeight: 500,
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          fontFamily: getMm(isDarkMode).fontBody,
                          px: { xs: 0.5, sm: 0 },
                        }}
                      >
                        {t("click_to_switch_profile_text")}
                      </Typography>
                    </Box>

                    <Grid container spacing={{ xs: 1.25, sm: 2.5 }} justifyContent="center">
                      {associatedAccounts.map(user => {
                        const isParent1 = user.parentID === parentAnchorID || String(user.parentID) === String(parentAnchorID);
                        const isParent2 = user.parentID2 === parentAnchorID || String(user.parentID2) === String(parentAnchorID);
                        const isPendingApproval = !user.isParent && (
                          (isParent1 && user.parentIDApproved !== true) ||
                          (isParent2 && user.parentID2Approved !== true)
                        );
                        return (
                        <Grid item xs={12} sm={6} key={user.studentID}>
                          <LandingMemberCard
                            user={user}
                            stats={memberStatsMap[user.studentID]}
                            variant="compact"
                            onClick={() => handleSwitchUser(user)}
                            unreadCount={unreadCountsByStudent[user.studentID] || 0}
                            isPendingApproval={isPendingApproval}
                            imageRefreshKey={imageRefreshKey}
                            isDarkMode={isDarkMode}
                            t={t}
                            isMobile={isMobile}
                            locale={i18n.language}
                          />
                        </Grid>
                      ); })}
                    </Grid>
                  </Box>
                )}

              </>
            )}
          </Box>
        </Fade>
      )}

      {showSwitchOverlay && <SwitchUserOverlay text={t("switching_user_text")} />}
    </Container>
  );
};

export default LandingPage;
